import React from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useSidebar } from '../../shared/components/Sidebar/SidebarProvider'
import { AnySidebarSection, SidebarNavigationItem, SidebarSection } from '../../shared/types/navigation'
import { getUrlForSidebarSection } from '../../shared/utils/sidebarNavigation'
import { useLogout } from '../../hooks/useLogout'
import useFirebaseUser from '../../hooks/useFirebaseUser'
import useAppState from '../../hooks/useAppState'
import ProfileBar from '../../components/ui/ProfileBar'
import { OrgRole } from '../../../../pure-js/types/OrganizationMembership'
import { isParticipantOrgRole } from '../../../../pure-js/libs/organizationMembershipHelpers'
import { orgRoleTranslationKey } from '../../features/organization/orgTranslationKeys'
import { getActiveOrgRole } from '../../features/organization/orgStateHelpers'
import { SidebarContainer } from './SidebarContainer'
import { SidebarNavigation } from './SidebarNavigation'
import { SidebarMenuList } from './SidebarMenuList'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'

/**
 * Props for the MaterialSidebar component
 */
export interface MaterialSidebarProps {
  navigationItems: SidebarNavigationItem[]
  /** Optional slot above the profile block (e.g. organization switcher). */
  profileTopSlot?: React.ReactNode
  /** Section IDs that should show the live activity badge */
  liveActivitySectionIds?: AnySidebarSection[]
  /** Section IDs that should show the "soon" blinking badge */
  soonActivitySectionIds?: AnySidebarSection[]
  /** Section IDs whose page holds feedback not read yet */
  newSectionIds?: AnySidebarSection[]
  notificationCountBySection?: Partial<Record<SidebarSection, number>>
  onDrawerClose?: () => void
}

/**
 * MaterialSidebar - Design system sidebar component matching exact Figma specifications
 *
 * **Component Structure:**
 * - `SidebarContainer`: Main wrapper with styling
 * - `ProfileBar`: User profile section
 * - `SidebarNavigation`: Navigation wrapper
 * - `SidebarMenuList`: List of menu items
 *   - `SidebarMenuItem`: Individual menu item
 *     - `ActiveIndicator`: Active bar indicator
 *     - `ActiveGlow`: Orange glow effect
 *
 * **Features:**
 * - Exact Figma CSS implementation
 * - Text gradient on active items
 * - Active indicator bar with glow
 * - Orange halo glow effect
 * - Embossed background
 * - Design system colors and spacing
 * - Role-based navigation filtering
 * - Profile section with logout
 *
 * **Design System Values:**
 * - Colors: `INTERAX_COLORS`
 * - Typography: `TYPOGRAPHY.menuSelected`, `TYPOGRAPHY.menuNonSelected`
 * - Spacing: `LAYOUT_SPACING.SIDEBAR_*`
 * - Dimensions: `COMPONENT_DIMENSIONS.*`
 */
export const MaterialSidebar: React.FC<MaterialSidebarProps> = ({
  navigationItems,
  profileTopSlot,
  liveActivitySectionIds,
  soonActivitySectionIds,
  newSectionIds,
  notificationCountBySection,
  onDrawerClose
}) => {
  const { setActiveSection } = useSidebar()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logout = useLogout(() => navigate('/'))
  const { data: firebaseUser } = useFirebaseUser()
  const { state } = useAppState()
  const activeOrgRole = getActiveOrgRole(state.user)
  const isAdmin = activeOrgRole === OrgRole.ADMIN
  const isParticipant = isParticipantOrgRole(activeOrgRole)
  const profileFallbackKey = isParticipant ? 'gamer' : 'facilitator'

  // Get user display name
  const userName =
    state.user?.displayName ||
    firebaseUser?.displayName ||
    state.user?.email?.split('@')[0] ||
    t(`${profileFallbackKey}.profile.${profileFallbackKey}`)

  const roleLabel = t(orgRoleTranslationKey(activeOrgRole ?? OrgRole.GAMER))

  // Handle account settings navigation
  const handleAccountSettings = () => {
    // Close drawer on mobile if provided
    if (onDrawerClose) {
      onDrawerClose()
    }

    if (isParticipant) {
      setActiveSection('account-settings' as SidebarSection)
      navigate('/gamer/account-settings')
    } else {
      setActiveSection(SidebarSection.PROFILE)
      navigate('/facilitator/profile')
    }
  }

  // Handle navigation item click
  const handleNavigationClick = (item: SidebarNavigationItem) => {
    if (!item.disabled) {
      // Close drawer on mobile if provided
      if (onDrawerClose) {
        onDrawerClose()
      }

      // If item has externalUrl, open in new tab
      if (item.externalUrl) {
        window.open(item.externalUrl, '_blank', 'noopener,noreferrer')
        return
      }
      // Otherwise, navigate internally
      setActiveSection(item.id)
      const url = getUrlForSidebarSection(item.id)
      navigate(url)
    }
  }

  return (
    <SidebarContainer dataTestId="facilitator-sidebar">
      {/* Profile Section */}
      <Box>
        {profileTopSlot}
        <ProfileBar
          name={userName}
          email={firebaseUser?.email || ''}
          role={roleLabel}
          onAccountSettings={handleAccountSettings}
          onLogout={logout}
        />
      </Box>

      {/* Main Navigation */}
      <SidebarNavigation>
        <SidebarMenuList
          navigationItems={navigationItems}
          liveActivitySectionIds={liveActivitySectionIds}
          soonActivitySectionIds={soonActivitySectionIds}
          newSectionIds={newSectionIds}
          notificationCountBySection={notificationCountBySection}
          isAdmin={isAdmin}
          onItemClick={handleNavigationClick}
        />
      </SidebarNavigation>
    </SidebarContainer>
  )
}

export default MaterialSidebar
